import _ from 'lodash';
import {connect} from 'react-redux';

import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
  wrapInShallowSelector,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {makeGetWillShowTriageForTeamAlert} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionSelectors';
import {
  ConversationStatusesEnum,
  makeFindConversationActionContext,
} from '../../../../../../helpers/models/conversationHelpers';
import {ResolveDispatchProps} from '../../../../../../helpers/reduxHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../../models/api/conversationModel';
import {deleteMessageTask} from '../../../../../../services/tasks/deleteMessageTask';
import {BulkActionsTrackingTypesEnum} from '../../../../../../services/tracking';
import {AppState} from '../../../../../../store/appState';
import {
  makeGetAreAllSpam,
  makeGetAreAllTrashed,
} from '../../../../../../store/data/conversations/conversationsMultiSelectors';
import {makeGetConversationContextNamespaces} from '../../../../../../store/data/conversations/conversationsSelectors';
import {
  setBulkConversationsStatusAction,
  setConversationsStatusAction,
} from '../../../../../../store/data/conversations/statusConversationsActions';
import {getMessages} from '../../../../../../store/data/dataSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {ConversationsTrashCoordinator} from './conversationsTrashCoordinator';

/*
 * Props.
 */

interface ConversationsTrashCoordinatorContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  /** Potentially partial set of currently selected conversation. */
  conversations: ReadonlyArray<ConversationBaseModel | ConversationModel>;
  isBulkActionRequired: boolean;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
  skipTopbarPage?: boolean;
}

/*
 * Selectors.
 */

/** Extract the cells context from the props. */
function getCellsContext(_state: AppState, props: ConversationsTrashCoordinatorContainerProps) {
  return props.cellsContext;
}

function getConversations(_state: unknown, props: ConversationsTrashCoordinatorContainerProps) {
  return props.conversations;
}

type DraftMessageUidsSelector<P> = AppParametricSelector<P, ReadonlyArray<string>>;
function makeGetDraftMessageUids() {
  return createSelector([getConversations], (conversations) =>
    _(conversations)
      // FIXME: We should be looking at the specific drafts selected in P2 rather than all drafts belonging to the conversations.
      .map((c) => ('draftMessageUids' in c ? c.draftMessageUids : []))
      .flatten()
      .value(),
  );
}

function makeGetDraftMessages<P>(getDraftMessageUids: DraftMessageUidsSelector<P>) {
  return createSelector([getDraftMessageUids, getMessages], (draftMessageUids, messages) =>
    _(draftMessageUids)
      .map((uid) => messages[uid])
      .compact()
      .value(),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationsTrashCoordinatorContainerProps>()(() => {
  const getDraftMessageUids = makeGetDraftMessageUids();

  return {
    contextNamespaces: wrapInShallowSelector(
      makeGetConversationContextNamespaces(getCellsContext, getConversations),
    ),
    draftMessageUids: getDraftMessageUids,
    draftMessages: makeGetDraftMessages(getDraftMessageUids),
    areAllTrashed: makeGetAreAllTrashed(getCellsContext, getConversations),
    areAllSpam: makeGetAreAllSpam(getCellsContext, getConversations),
    shouldShowTrashTeamAlert: makeGetWillShowTriageForTeamAlert(
      getCellsContext,
      getConversations,
      TriageActionsEnum.TRASH,
    ),
    shouldShowSpamTeamAlert: makeGetWillShowTriageForTeamAlert(
      getCellsContext,
      getConversations,
      TriageActionsEnum.SPAM,
    ),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ConversationsTrashCoordinatorContainerProps>()((props) => ({
  setConversationsStatus: setConversationsStatusAction,
  setBulkConversationsStatus: setBulkConversationsStatusAction,
  deleteMessage: deleteMessageTask,
}));

// TODO: Migrate this into mapDispatchToProps since it does not depend on stateProps.
function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ConversationsTrashCoordinatorContainerProps,
) {
  const {cellsContext, inboxesParameters, conversations, onInterfaceEvent} = ownProps;
  // TODO: update this to be selection to support bulk actions
  const conversationIds = conversations.map((c) => c.id);

  const onTrash = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.TRASH, false);
    }

    dispatchProps.setConversationsStatus(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.TRASHED,
      makeFindConversationActionContext(cellsContext, conversations),
    );
  };

  const onSpam = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_SPAM, false);
    }

    dispatchProps.setConversationsStatus(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.SPAM,
      makeFindConversationActionContext(cellsContext, conversations),
    );
  };

  const onReopen = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.RESTORE, false);
    }

    dispatchProps.setConversationsStatus(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.OPEN,
      makeFindConversationActionContext(cellsContext, conversations),
    );
  };

  const onBulkTrash = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.TRASH, true);
    }

    dispatchProps.setBulkConversationsStatus(
      inboxesParameters,
      cellsContext,
      ConversationStatusesEnum.TRASHED,
    );
  };

  const onBulkSpam = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_SPAM, true);
    }

    dispatchProps.setBulkConversationsStatus(inboxesParameters, cellsContext, ConversationStatusesEnum.SPAM);
  };

  const onBulkReopen = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.RESTORE, true);
    }

    dispatchProps.setBulkConversationsStatus(inboxesParameters, cellsContext, ConversationStatusesEnum.OPEN);
  };

  const onDeleteMessageClick = (messageUid: string) => {
    dispatchProps.deleteMessage(messageUid);
  };

  return {
    ...ownProps,
    ...stateProps,
    onTrash,
    onSpam,
    onReopen,
    onBulkTrash,
    onBulkSpam,
    onBulkReopen,
    onDeleteMessageClick,
  };
}

export const ConversationsTrashCoordinatorContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ConversationsTrashCoordinator);
